import React from 'react';
import './App.css';

const App: React.FC = () => {
  return (
    <div className="App">
      {/* Header */}
      <header className="hero">
        <div className="container">
          <h1 className="hero-title">🚀 CEMA AI Investment System</h1>
          <p className="hero-subtitle">Transform Your Investment Management with AI</p>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">18</span>
              <span className="stat-label">Month Timeline</span>
            </div>
            <div className="stat">
              <span className="stat-number">85%</span>
              <span className="stat-label">Prediction Accuracy</span>
            </div>
            <div className="stat">
              <span className="stat-number">24/7</span>
              <span className="stat-label">AI Monitoring</span>
            </div>
          </div>
        </div>
      </header>

      {/* Transformation Section */}
      <section className="transformation">
        <div className="container">
          <h2>Current State → Future State</h2>
          <div className="transformation-grid">
            <div className="before-state">
              <h3>BEFORE</h3>
              <div className="state-items">
                <div className="state-item">📊 Manual QuickBooks Import</div>
                <div className="state-item">📅 Monthly Risk Reviews</div>
                <div className="state-item">🚨 Reactive Problem Solving</div>
              </div>
            </div>
            <div className="arrow">→</div>
            <div className="after-state">
              <h3>AFTER</h3>
              <div className="state-items">
                <div className="state-item">🤖 AI Dashboard</div>
                <div className="state-item">⚡ Real-time Alerts</div>
                <div className="state-item">🔮 Predictive Analytics</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="portfolio">
        <div className="container">
          <h2>Portfolio AI Agents</h2>
          <div className="portfolio-grid">
            <div className="portfolio-card real-estate">
              <h3>🏢 Real Estate</h3>
              <div className="percentage">50%</div>
              <ul className="capabilities">
                <li>Property Value AI</li>
                <li>Market Trends Analysis</li>
                <li>Tenant Risk Assessment</li>
              </ul>
            </div>
            <div className="portfolio-card gap-coverage">
              <h3>🚑 Gap Coverage</h3>
              <div className="percentage">25%</div>
              <ul className="capabilities">
                <li>Healthcare Market AI</li>
                <li>Accident Trend Forecasting</li>
                <li>Legal Partner Performance</li>
              </ul>
            </div>
            <div className="portfolio-card business-lending">
              <h3>💼 Business Lending</h3>
              <div className="percentage">25%</div>
              <ul className="capabilities">
                <li>Credit Risk AI</li>
                <li>Default Prediction</li>
                <li>Collateral Monitoring</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Data Flow Section */}
      <section className="data-flow">
        <div className="container">
          <h2>Data Flow Architecture</h2>
          <div className="flow-diagram">
            <div className="flow-row">
              <div className="flow-box source">QuickBooks</div>
              <div className="flow-box source">Market Data</div>
              <div className="flow-box source">Credit Bureaus</div>
            </div>
            <div className="flow-arrow-down">↓</div>
            <div className="flow-row">
              <div className="flow-box hub">Microsoft Fabric</div>
            </div>
            <div className="flow-arrow-down">↓</div>
            <div className="flow-row">
              <div className="flow-box processing">AI Agents</div>
            </div>
            <div className="flow-arrow-down">↓</div>
            <div className="flow-row">
              <div className="flow-box output">Executive Dashboard</div>
              <div className="flow-box output">Mobile Alerts</div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline">
        <div className="container">
          <h2>3-Phase Implementation</h2>
          <div className="timeline-grid">
            <div className="phase-card phase-1">
              <h3>Phase 1: Foundation</h3>
              <div className="phase-duration">Months 1-3</div>
              <ul>
                <li>✅ QuickBooks Connection</li>
                <li>✅ Basic Dashboard</li>
                <li>✅ Risk Alerts</li>
              </ul>
              <div className="phase-result">
                "Chicago property rent ↓15% vs market ↓3% - check tenant"
              </div>
            </div>
            <div className="phase-card phase-2">
              <h3>Phase 2: Intelligence</h3>
              <div className="phase-duration">Months 4-12</div>
              <ul>
                <li>🤖 Specialized AI Agents</li>
                <li>📊 Predictive Modeling</li>
                <li>⚠️ Early Warning System</li>
              </ul>
              <div className="phase-result">
                "Manufacturing borrower: 78% default risk - review now"
              </div>
            </div>
            <div className="phase-card phase-3">
              <h3>Phase 3: Advanced AI</h3>
              <div className="phase-duration">Months 13-18</div>
              <ul>
                <li>💬 Natural Language Queries</li>
                <li>🧠 Strategic AI Recommendations</li>
                <li>🎯 Competitive Advantage</li>
              </ul>
              <div className="phase-result">
                "RE peaking - reduce 50%→40%, increase gap coverage"
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example Briefing */}
      <section className="briefing">
        <div className="container">
          <h2>Daily AI Briefing Example</h2>
          <div className="briefing-card">
            <div className="briefing-header">🌅 Morning Report</div>
            <div className="briefing-content">
              "Portfolio +2.3% this week. 3 properties showing strong performance, 1 requires attention. 
              Gap coverage pipeline: 12 new opportunities identified. Business lending: 2 early warning alerts generated."
            </div>
            <div className="briefing-actions">
              📋 <strong>Actions:</strong> Review Chicago property lease renewal, investigate Detroit manufacturing sector stress indicators.
            </div>
          </div>
          <div className="alert-card">
            <div className="alert-header">🚨 Real-time Alert</div>
            <div className="alert-content">
              "XYZ Corp missed payment. Risk score increased to 78%. Collateral review initiated. Recovery plan generated."
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <h2>Key Benefits</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <h3>🎯 Proactive Risk Management</h3>
              <p>Identify problems 30-60 days before they impact your portfolio</p>
            </div>
            <div className="benefit-card">
              <h3>⚡ Real-time Monitoring</h3>
              <p>24/7 automated surveillance of all investments and market conditions</p>
            </div>
            <div className="benefit-card">
              <h3>📊 Predictive Analytics</h3>
              <p>85% accuracy in default prediction and market trend forecasting</p>
            </div>
            <div className="benefit-card">
              <h3>🤖 AI-Powered Insights</h3>
              <p>Natural language queries and strategic investment recommendations</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Next Steps</h2>
          <div className="cta-content">
            <div className="cta-step">
              <span className="step-number">1</span>
              <span className="step-text">Start Small: Phase 1 pilot (QuickBooks + dashboard)</span>
            </div>
            <div className="cta-step">
              <span className="step-number">2</span>
              <span className="step-text">Measure Results: Track time savings and problem detection</span>
            </div>
            <div className="cta-step">
              <span className="step-number">3</span>
              <span className="step-text">Scale Up: Add AI agents based on demonstrated success</span>
            </div>
          </div>
          <div className="cta-summary">
            <strong>Timeline:</strong> 18 months total, benefits start Month 1<br/>
            <strong>Goal:</strong> Transform from reactive to predictive investment management
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>© 2025 CEMA AI Investment System - Transforming Portfolio Management with AI</p>
        </div>
      </footer>
    </div>
  );
};

export default App;